import { atomEffect } from "jotai-effect";
import { atomFamily, loadable } from "jotai/utils";

import { $personalWorkspace } from "../../workspaces";
import { $remoteWorkspace } from "../sharing";
import { $pendingPermissionsAtom } from "../sharing/pending-permissions.atom";
import {
	$loadableSyncCurrentSnapshot,
	$plannedOutcomePublish,
	$plannedOutcomeShareSync,
} from "./current-state.workflow.atom";
import { $executePublishAndPushWorkflow } from "./publish.workflow.atom";
import { $executeShareCollabWorkflow } from "./share-collab.workflow.atom";
import { $setPublishPlan, $setSharePlan, $skipCollab, $syncState } from "./sync-state.atom";

// Pre-defined loadable wrappers (must be outside effect to avoid creating new atoms each run)
const $loadablePlannedOutcomePublish = atomFamily((documentId: string | undefined) =>
	loadable($plannedOutcomePublish(documentId))
);

const $loadablePlannedOutcomeShare = atomFamily((documentId: string | undefined) =>
	loadable($plannedOutcomeShareSync(documentId))
);

const $loadableRemoteWorkspace = atomFamily((documentId: string | undefined) =>
	loadable($remoteWorkspace(documentId))
);

// Per-document locks to prevent concurrent plan generation
const generatingPublishByDocument = new Map<string, boolean>();
const generatingShareByDocument = new Map<string, boolean>();

/**
 * atomEffect that automatically generates the publish plan when dependencies change.
 * Uses a lock to prevent concurrent dry-runs from interleaving.
 */
export const $publishPlanEffect = atomFamily((documentId: string | undefined) => {
	const effect = atomEffect((get, set) => {
		if (!documentId) {
			set($setPublishPlan, { documentId: "", plan: [] });
			return;
		}

		// Don't regenerate plan while sync is in progress
		const syncState = get($syncState(documentId));
		if (syncState.isSyncing) {
			return;
		}

		const snapshot = get($loadableSyncCurrentSnapshot(documentId));
		const outcome = get($loadablePlannedOutcomePublish(documentId));
		const remoteWorkspace = get($loadableRemoteWorkspace(documentId));
		const personalWorkspace = get($personalWorkspace);

		// Not ready yet
		if (snapshot.state !== "hasData" || outcome.state !== "hasData") {
			return;
		}

		// Can't execute - clear plan
		if (!outcome.data.canExecute) {
			set($setPublishPlan, { documentId, plan: [] });
			return;
		}

		// For unpublished docs, we need a workspace - use personal workspace as fallback
		const effectiveWorkspace =
			remoteWorkspace.state === "hasData" && remoteWorkspace.data
				? remoteWorkspace.data
				: personalWorkspace;

		// If no workspace available and remote repo doesn't exist, can't generate plan
		if (!effectiveWorkspace && !snapshot.data.remoteRepoExists) {
			return;
		}

		// Skip if already generating for this document
		if (generatingPublishByDocument.get(documentId)) {
			return;
		}

		generatingPublishByDocument.set(documentId, true);

		set($executePublishAndPushWorkflow, {
			snap: snapshot.data,
			plannedOutcome: outcome.data,
			commitMessage: "",
			workspace: effectiveWorkspace,
			dryRun: true,
		}).finally(() => {
			generatingPublishByDocument.delete(documentId);
		});
	});
	effect.debugLabel = `publishPlanEffect/${documentId}`;
	return effect;
});

/**
 * atomEffect that automatically generates the share plan when dependencies change.
 * Uses a lock to prevent concurrent dry-runs from interleaving.
 */
export const $sharePlanEffect = atomFamily((documentId: string | undefined) => {
	const effect = atomEffect((get, set) => {
		if (!documentId) {
			set($setSharePlan, { documentId: "", plan: [] });
			return;
		}

		// Don't regenerate plan while sync is in progress
		const syncState = get($syncState(documentId));
		if (syncState.isSyncing) {
			return;
		}

		const snapshot = get($loadableSyncCurrentSnapshot(documentId));
		const outcome = get($loadablePlannedOutcomeShare(documentId));
		const remoteWorkspace = get($loadableRemoteWorkspace(documentId));
		const personalWorkspace = get($personalWorkspace);
		// Subscribe to pending permissions - this will regenerate the plan when permissions change
		const pendingPermissions = get($pendingPermissionsAtom);
		// Subscribe to skipCollab toggle - regenerate plan when it changes
		const skipCollab = get($skipCollab(documentId));

		// Not ready yet
		if (snapshot.state !== "hasData" || outcome.state !== "hasData") {
			return;
		}

		// Can't execute - clear plan
		if (!outcome.data.canExecute) {
			set($setSharePlan, { documentId, plan: [] });
			return;
		}

		// For unpublished docs, we need a workspace - use personal workspace as fallback
		const effectiveWorkspace =
			remoteWorkspace.state === "hasData" && remoteWorkspace.data
				? remoteWorkspace.data
				: personalWorkspace;

		// If no workspace available and remote repo doesn't exist, can't generate plan
		if (!effectiveWorkspace && !snapshot.data.remoteRepoExists) {
			return;
		}

		// Skip if already generating for this document
		if (generatingShareByDocument.get(documentId)) {
			return;
		}

		generatingShareByDocument.set(documentId, true);

		set($executeShareCollabWorkflow, {
			snap: snapshot.data,
			plannedOutcome: outcome.data,
			pendingPermissions,
			workspace: effectiveWorkspace,
			dryRun: true,
			skipCollab,
		}).finally(() => {
			generatingShareByDocument.delete(documentId);
		});
	});
	effect.debugLabel = `sharePlanEffect/${documentId}`;
	return effect;
});
